'use client';

import React, { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import FilterBar from './FilterBar';
import ServiceDetailModal from './ServiceDetailModal';
import QuickContactBar from './QuickContactBar';

interface Service {
  id: number;
  title: string;
  description: string;
  fullDescription: string;
  price: string;
  image: string;
  alt: string;
  category: 'health' | 'community';
  location: string[];
  features: string[];
  eligibility: string[];
  successStory: {
    name: string;
    story: string;
    image: string;
    alt: string;
  };
}

const ServicesInteractive: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
  {
    id: 1,
    title: 'Comprehensive Health Checkup',
    description: 'Complete health screening with blood tests, ECG, and doctor consultation at relief rates.',
    fullDescription:
    'Our comprehensive health checkup package includes complete blood count, lipid profile, liver function tests, kidney function tests, blood sugar tests, ECG, chest X-ray, and consultation with experienced physicians. All tests are conducted using modern equipment with accurate results delivered within 24 hours.',
    price: '₹499',
    image: "https://images.unsplash.com/photo-1666886573600-61c634663827",
    alt: 'Medical professional in white coat examining patient health reports with stethoscope on desk',
    category: 'health',
    location: ['Sarthana', 'Varachha', 'Kadodara'],
    features: [
    'Complete Blood Count (CBC)',
    'Lipid Profile & Blood Sugar Tests',
    'ECG & Chest X-Ray',
    'Doctor Consultation Included',
    'Reports within 24 hours',
    'Free follow-up consultation'],

    eligibility: [
    'Open to all age groups above 18 years',
    'Valid ID proof required',
    'Prior appointment recommended',
    'Fasting required for certain tests'],

    successStory: {
      name: 'Ramesh Patel',
      story:
      'The affordable health checkup helped me detect diabetes early. The doctors guided me with proper treatment and lifestyle changes. I am grateful for their compassionate care.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1927cf9a3-1763293370228.png",
      alt: 'Middle-aged Indian man with short black hair wearing blue shirt smiling at camera'
    }
  },
  {
    id: 2,
    title: 'Dental Care Services',
    description: 'Affordable dental checkups, cleaning, and basic treatments for maintaining oral health.',
    fullDescription:
    'Our dental care services include routine checkups, professional cleaning, cavity fillings, tooth extractions, and basic root canal treatments. We use modern dental equipment and maintain strict hygiene standards to ensure safe and comfortable treatment for all patients.',
    price: '₹299',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5a15cb3-1767891057902.png",
    alt: 'Dentist in blue scrubs examining patient teeth with dental tools in modern clinic',
    category: 'health',
    location: ['Sarthana', 'Varachha'],
    features: [
    'Dental Checkup & Consultation',
    'Professional Teeth Cleaning',
    'Cavity Filling & Extractions',
    'Basic Root Canal Treatment',
    'Oral Hygiene Education',
    'Emergency Dental Care'],

    eligibility: [
    'Available for all age groups',
    'Children must be accompanied by guardian',
    'Appointment required for treatments',
    'Emergency cases handled immediately'],

    successStory: {
      name: 'Priya Shah',
      story:
      'I was suffering from severe toothache but could not afford expensive dental treatment. Seva Setu provided excellent care at affordable rates. The dentist was very gentle and caring.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19be4cc4f-1763296487049.png",
      alt: 'Young Indian woman with long black hair wearing white top smiling warmly'
    }
  },
  {
    id: 3,
    title: 'Eye Care & Vision Testing',
    description: 'Complete eye examination with vision testing and affordable prescription glasses.',
    fullDescription:
    'Our eye care services include comprehensive eye examination, vision testing, retinal screening, glaucoma testing, and cataract evaluation. We also provide affordable prescription glasses and contact lenses. Our experienced optometrists ensure accurate diagnosis and appropriate treatment recommendations.',
    price: '₹199',
    image: "https://images.unsplash.com/photo-1724215618752-b445efb38b77",
    alt: 'Optometrist conducting eye examination on patient using modern vision testing equipment',
    category: 'health',
    location: ['Sarthana', 'Kadodara'],
    features: [
    'Complete Eye Examination',
    'Vision Testing & Refraction',
    'Retinal Screening',
    'Glaucoma & Cataract Check',
    'Affordable Prescription Glasses',
    'Contact Lens Consultation'],

    eligibility: [
    'All age groups welcome',
    'Children eye checkup available',
    'Senior citizen special care',
    'Diabetic retinopathy screening'],

    successStory: {
      name: 'Kiran Desai',
      story:
      'My vision was deteriorating but I could not afford expensive eye care. Seva Setu not only provided thorough examination but also helped me get affordable glasses. Now I can see clearly and work comfortably.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_123682e34-1767527811620.png",
      alt: 'Elderly Indian man with gray hair and glasses wearing traditional kurta smiling gently'
    }
  },
  {
    id: 4,
    title: 'Clothing Distribution Drive',
    description: 'Free clothing distribution for underprivileged families and individuals in need.',
    fullDescription:
    'Our clothing distribution program collects gently used and new clothes from donors and distributes them to families in need. We organize regular distribution drives in different localities, ensuring dignified distribution of quality clothing for all age groups and seasons.',
    price: 'Free',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1125a79cf-1767787362151.png",
    alt: 'Volunteers organizing colorful donated clothes on tables for community distribution event',
    category: 'community',
    location: ['Sarthana', 'Varachha', 'Kadodara'],
    features: [
    'Free Clothing for All Ages',
    'Seasonal Clothing Available',
    'Dignified Distribution Process',
    'Quality Checked Garments',
    'Regular Distribution Drives',
    'Home Delivery for Elderly'],

    eligibility: [
    'Low-income families',
    'Homeless individuals',
    'Disaster-affected families',
    'Valid ID proof required'],

    successStory: {
      name: 'Meena Sharma',
      story:
      'After losing everything in a fire, Seva Setu provided my family with clothes and essentials. The volunteers treated us with respect and dignity. We are forever grateful for their support.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_118f6d647-1763298588604.png",
      alt: 'Indian woman in her thirties with black hair wearing red saree smiling with gratitude'
    }
  },
  {
    id: 5,
    title: 'Community Food Distribution',
    description: 'Nutritious meal distribution for homeless and underprivileged community members.',
    fullDescription:
    'We prepare and distribute fresh, nutritious meals to homeless individuals and underprivileged families. Our community kitchen operates daily, serving breakfast and dinner. We also provide food packets during festivals and special occasions, ensuring no one in our community goes hungry.',
    price: 'Free',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_140936fb0-1764668127268.png",
    alt: 'Volunteers serving hot nutritious meals to people in community food distribution center',
    category: 'community',
    location: ['Sarthana', 'Varachha'],
    features: [
    'Fresh Nutritious Meals Daily',
    'Breakfast & Dinner Service',
    'Festival Special Meals',
    'Hygienic Food Preparation',
    'Takeaway Food Packets',
    'Special Diet Accommodation'],

    eligibility: [
    'Homeless individuals',
    'Daily wage workers',
    'Elderly without family support',
    'No documentation required'],

    successStory: {
      name: 'Raju Bhai',
      story:
      'As a daily wage worker, some days I could not afford food. Seva Setu community kitchen has been a blessing. The food is delicious and served with love. I never feel like a burden.',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8d5b425-1763295447954.png",
      alt: 'Middle-aged Indian man with mustache wearing casual shirt showing grateful expression'
    }
  },
  {
    id: 6,
    title: 'Fitness & Yoga Classes',
    description: 'Free yoga and fitness classes for community wellness and healthy living.',
    fullDescription:
    'Our fitness program includes daily yoga sessions, meditation classes, and basic fitness training. Led by certified instructors, these classes focus on holistic wellness, stress management, and healthy lifestyle practices. Special sessions are organized for seniors, women, and children.',
    price: 'Free',
    image: "https://images.unsplash.com/photo-1687255735591-42220cebc2fe",
    alt: 'Group of diverse people practicing yoga poses on mats in outdoor park setting',
    category: 'community',
    location: ['Sarthana', 'Kadodara'],
    features: [
    'Daily Yoga Sessions',
    'Meditation & Breathing Exercises',
    'Basic Fitness Training',
    'Senior Citizen Special Classes',
    'Women-Only Sessions',
    'Children Yoga Programs'],

    eligibility: [
    'Open to all age groups',
    'No prior experience needed',
    'Registration required',
    'Regular attendance encouraged'],

    successStory: {
      name: 'Anjali Mehta',
      story:
      'The yoga classes have transformed my life. I was suffering from stress and back pain. Regular practice has improved my health significantly. The instructors are knowledgeable and caring.',
      image: "https://images.unsplash.com/flagged/photo-1565939627066-aff72b006535",
      alt: 'Young Indian woman with tied hair in yoga attire sitting peacefully in meditation pose'
    }
  }];


  const filteredServices = services.filter((service) => {
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    const matchesLocation =
    selectedLocation === 'all' ||
    service.location.some((loc) => loc.toLowerCase() === selectedLocation);
    const matchesSearch =
    searchQuery === '' ||
    service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesLocation && matchesSearch;
  });

  const handleServiceClick = (service: Service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleContact = () => {
    if (!isHydrated) return;
    window.location.href = 'tel:+918758758778';
  };

  const handleWhatsApp = () => {
    if (!isHydrated) return;
    window.open('https://wa.me/918758758778', '_blank');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-muted rounded-xl" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-xl" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <FilterBar
          selectedCategory={selectedCategory}
          selectedLocation={selectedLocation}
          searchQuery={searchQuery}
          onCategoryChange={setSelectedCategory}
          onLocationChange={setSelectedLocation}
          onSearchChange={setSearchQuery} />


        {filteredServices.length === 0 ?
        <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <span className="text-3xl">🔍</span>
            </div>
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">No Services Found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or search query to find what you&apos;re looking for.
            </p>
            <button
            onClick={() => {
              setSelectedCategory('all');
              setSelectedLocation('all');
              setSearchQuery('');
            }}
            className="px-6 py-2.5 rounded-lg font-heading font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300">

              Clear All Filters
            </button>
          </div> :

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) =>
          <div key={service.id} onClick={() => handleServiceClick(service)} className="cursor-pointer">
                <ServiceCard
              title={service.title}
              description={service.description}
              price={service.price}
              image={service.image}
              alt={service.alt}
              category={service.category}
              location={service.location}
              features={service.features}
              onContact={(e) => {
                e.stopPropagation();
                handleContact();
              }}
              onWhatsApp={(e) => {
                e.stopPropagation();
                handleWhatsApp();
              }} />

              </div>
          )}
          </div>
        }
      </div>

      <ServiceDetailModal
        service={selectedService}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onContact={handleContact}
        onWhatsApp={handleWhatsApp} />


      <QuickContactBar />
    </>);

};

export default ServicesInteractive;